/**
 * Alta access token cookie access.
 *
 * Ported from Platinum `app/utils/accessToken.ts`, with one deliberate
 * substitution: Platinum reads and writes the cookie through the
 * `universal-cookie` package, which this app does not depend on. These helpers
 * use `document.cookie` directly so no new dependency is added.
 *
 * Read and delete only. The portal never issues an Alta token — Platinum's
 * sign-in page sets it on `.alta.avigilon.com`, and this app is a reader of
 * that shared cookie.
 */
import { getAccessTokenCookieName } from './altaEnv';

// Root domain the cookie is scoped to. Platinum sets it here rather than on a
// single host so sibling apps across the estate share one session.
const COOKIE_DOMAIN = '.alta.avigilon.com';

/**
 * Reads a raw cookie value by name.
 *
 * @param {string} name
 * @returns {string|undefined} undefined when the cookie is absent or empty.
 */
const readCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) return undefined;

  // Split on ';' and trim rather than on '; '. Browsers serialise the pairs
  // with a space after the semicolon, but trimming does not rely on that.
  //
  // The prefix includes the '=' so the match is on the whole name: looking for
  // `accessToken` must not return `accessToken-dev`. That matters here, because
  // production uses the bare name while every other env uses a suffixed one.
  const prefix = `${name}=`;
  const match = document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(prefix));

  if (!match) return undefined;

  const value = match.slice(prefix.length);
  if (!value) return undefined;

  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed percent-escape would throw. The token is unusable either
    // way, so report it as absent and let the caller redirect to sign-in.
    return undefined;
  }
};

/**
 * The Alta access token for the current environment.
 *
 * @param {string} opEnv OP_ENV from `getOpEnv()`.
 * @returns {string|undefined}
 */
export const getAccessToken = (opEnv) => {
  const cookieName = getAccessTokenCookieName(opEnv);
  if (!cookieName) return undefined;

  return readCookie(cookieName);
};

/**
 * Deletes the Alta access token cookie so a rejected token is not retried on
 * the next load.
 *
 * Both a domain-scoped and a host-scoped delete are issued. A cookie can only
 * be removed by a request whose domain and path match the one it was set with,
 * and Platinum documents a second copy appearing on the specific host —
 * written by a CloudFront function during app switching. Leaving that copy
 * behind is what causes Platinum's redirect loop, where the stale token is read
 * back and treated as a session.
 *
 * @param {string} opEnv OP_ENV from `getOpEnv()`.
 */
export const removeAccessToken = (opEnv) => {
  const cookieName = getAccessTokenCookieName(opEnv);
  if (!cookieName || typeof document === 'undefined') return;

  const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';

  // Domain-scoped copy, written by Platinum's sign-in page.
  document.cookie = `${cookieName}=; expires=${expired}; path=/; domain=${COOKIE_DOMAIN}`;

  // Host-scoped copy, written by the CloudFront function noted above.
  document.cookie = `${cookieName}=; expires=${expired}; path=/`;
};
